<template>
  <div
    ref="floating"
    class="floating-repair-select-wrapper"
    :class="{ 'is-dragging': isDragging }"
    :style="wrapperStyle"
    @pointerdown="startDrag"
    @click="handleClick"
  >
    <div
      class="tooltip"
      :class="{
        'tooltip--below': isTooltipBelow,
        'tooltip--left': isTooltipLeft,
      }"
    >
      {{ $t(tooltipText) }}
    </div>
    <button class="floating-repair-select-button" @dragstart.prevent>
      <img
        src="/img/icons/rs_icon.png"
        :alt="$t('repair_select')"
        draggable="false"
      />
    </button>
  </div>
</template>

<script>
export default {
  name: 'floating-repair-select',
  props: {
    tooltipText: {
      type: String,
      default: 'shared.chat_with_rs',
    },
    storageKeyBase: {
      type: String,
      default: 'floatingRepairSelect',
    },
    compactBreakpoint: {
      type: Number,
      default: 1024,
    },
  },
  data() {
    return {
      position: { x: 0, y: 0 },
      dragState: null,
      isDragging: false,
      dragMoved: false,
      suppressClick: false,
      viewportWidth: window.innerWidth,
      viewportHeight: window.innerHeight,
    };
  },
  computed: {
    wrapperStyle() {
      return {
        left: `${this.position.x}px`,
        top: `${this.position.y}px`,
        right: 'auto',
        bottom: 'auto',
      };
    },
    isTooltipBelow() {
      return this.position.y < 120;
    },
    isTooltipLeft() {
      const centerX = this.position.x + this.getButtonSize() / 2;
      return centerX < this.viewportWidth / 2;
    },
  },
  mounted() {
    this.initPosition();
    window.addEventListener('resize', this.handleResize);

    this.onDragMove = this.onDragMove.bind(this);
    this.endDrag = this.endDrag.bind(this);
  },
  beforeDestroy() {
    window.removeEventListener('resize', this.handleResize);
    this.removeDragListeners();
  },
  methods: {
    handleClick() {
      if (this.suppressClick) {
        this.suppressClick = false;
        return;
      }
      this.$emit('open');
    },
    initPosition() {
      const stored = this.loadPosition();
      if (stored) {
        this.position = this.clampPosition(stored.x, stored.y);
        return;
      }
      const fallback = this.getDefaultPosition();
      this.position = this.clampPosition(fallback.x, fallback.y);
      this.savePosition();
    },
    handleResize() {
      const previousKey = this.getStorageKey();
      this.viewportWidth = window.innerWidth;
      this.viewportHeight = window.innerHeight;
      const nextKey = this.getStorageKey();

      if (previousKey !== nextKey && !this.loadPosition()) {
        const fallback = this.getDefaultPosition();
        this.position = this.clampPosition(fallback.x, fallback.y);
        this.savePosition();
        return;
      }

      this.position = this.clampPosition(this.position.x, this.position.y);
      this.savePosition();
    },
    getStorageKey() {
      const bucket =
        this.viewportWidth <= this.compactBreakpoint ? 'compact' : 'desktop';
      return `${this.storageKeyBase}:${bucket}`;
    },
    loadPosition() {
      try {
        const raw = localStorage.getItem(this.getStorageKey());
        return raw ? JSON.parse(raw) : null;
      } catch (error) {
        return null;
      }
    },
    savePosition() {
      localStorage.setItem(this.getStorageKey(), JSON.stringify(this.position));
    },
    getDefaultPosition() {
      const size = this.getButtonSize();
      if (this.viewportWidth <= this.compactBreakpoint) {
        return { x: 50, y: 160 };
      }
      return {
        x: Math.max(0, this.viewportWidth - size - 200),
        y: Math.max(0, this.viewportHeight - size - 200),
      };
    },
    getButtonSize() {
      const el = this.$refs.floating;
      if (el && el.getBoundingClientRect) {
        const rect = el.getBoundingClientRect();
        return Math.max(rect.width || 0, rect.height || 0) || 55;
      }
      return 55;
    },
    clampPosition(x, y) {
      const size = this.getButtonSize();
      const maxX = Math.max(0, this.viewportWidth - size);
      const maxY = Math.max(0, this.viewportHeight - size);
      return {
        x: Math.min(Math.max(x, 0), maxX),
        y: Math.min(Math.max(y, 0), maxY),
      };
    },
    getEventPoint(event) {
      if (event.touches && event.touches.length) {
        return { x: event.touches[0].clientX, y: event.touches[0].clientY };
      }
      if (event.changedTouches && event.changedTouches.length) {
        return {
          x: event.changedTouches[0].clientX,
          y: event.changedTouches[0].clientY,
        };
      }
      return { x: event.clientX, y: event.clientY };
    },
    startDrag(event) {
      if (event.button !== 0) return;

      const point = this.getEventPoint(event);
      this.dragState = {
        startX: point.x,
        startY: point.y,
        originX: this.position.x,
        originY: this.position.y,
        pointerId: event.pointerId,
        type: event.type,
        captureTarget: event.currentTarget,
      };
      this.isDragging = true;
      this.dragMoved = false;

      if (event.type === 'pointerdown') {
        const target = event.currentTarget || event.target;
        if (target && target.setPointerCapture && event.pointerId) {
          target.setPointerCapture(event.pointerId);
        }
        window.addEventListener('pointermove', this.onDragMove);
        window.addEventListener('pointerup', this.endDrag);
        window.addEventListener('pointercancel', this.endDrag);
        window.addEventListener('blur', this.endDrag);
        return;
      }

      if (event.type === 'touchstart') {
        window.addEventListener('touchmove', this.onDragMove, {
          passive: false,
        });
        window.addEventListener('touchend', this.endDrag);
        window.addEventListener('touchcancel', this.endDrag);
        window.addEventListener('blur', this.endDrag);
        return;
      }

      window.addEventListener('mousemove', this.onDragMove);
      window.addEventListener('mouseup', this.endDrag);
      window.addEventListener('blur', this.endDrag);
    },
    onDragMove(event) {
      if (!this.dragState) return;
      if (event.cancelable) {
        event.preventDefault();
      }
      const point = this.getEventPoint(event);
      const deltaX = point.x - this.dragState.startX;
      const deltaY = point.y - this.dragState.startY;

      if (!this.dragMoved) {
        const distance = Math.hypot(deltaX, deltaY);
        if (distance < 6) return;
        this.dragMoved = true;
      }

      this.position = this.clampPosition(
        this.dragState.originX + deltaX,
        this.dragState.originY + deltaY
      );
    },
    endDrag() {
      if (
        this.dragState &&
        this.dragState.type === 'pointerdown' &&
        this.dragState.captureTarget?.releasePointerCapture
      ) {
        try {
          this.dragState.captureTarget.releasePointerCapture(
            this.dragState.pointerId
          );
        } catch (error) {
          // ignore
        }
      }

      this.removeDragListeners();

      if (this.dragMoved) {
        this.suppressClick = true;
        this.savePosition();
        setTimeout(() => {
          this.suppressClick = false;
        }, 150);
      }

      this.isDragging = false;
      this.dragState = null;
    },
    removeDragListeners() {
      window.removeEventListener('pointermove', this.onDragMove);
      window.removeEventListener('pointerup', this.endDrag);
      window.removeEventListener('pointercancel', this.endDrag);
      window.removeEventListener('touchmove', this.onDragMove);
      window.removeEventListener('touchend', this.endDrag);
      window.removeEventListener('touchcancel', this.endDrag);
      window.removeEventListener('mousemove', this.onDragMove);
      window.removeEventListener('mouseup', this.endDrag);
      window.removeEventListener('blur', this.endDrag);
    },
  },
};
</script>

<style lang="scss" scoped>
.floating-repair-select-wrapper {
  position: fixed;
  width: 55px;
  height: 55px;
  z-index: 1000;
  cursor: grab;
  touch-action: none;
  user-select: none;
}

.floating-repair-select-wrapper.is-dragging {
  cursor: grabbing;
}

.floating-repair-select-wrapper:hover .tooltip {
  opacity: 1;
  visibility: visible;
  transform: translateY(0);
}

.tooltip {
  position: absolute;
  bottom: calc(100% + 15px);
  right: 0;
  transform: translateY(10px);
  background-color: #2c3e50;
  color: #fff;
  padding: 10px 16px;
  border-radius: 8px;
  font-size: 14px;
  font-weight: 500;
  white-space: nowrap;
  box-shadow: 0 4px 15px rgba(0, 0, 0, 0.2);
  opacity: 0;
  visibility: hidden;
  transition: all 0.3s cubic-bezier(0.165, 0.84, 0.44, 1);
  pointer-events: none;
}

.tooltip::after {
  content: '';
  position: absolute;
  top: 100%;
  right: 22px;
  border-width: 6px;
  border-style: solid;
  border-color: #2c3e50 transparent transparent transparent;
}

.tooltip.tooltip--left {
  left: 0;
  right: auto;
}

.tooltip.tooltip--left::after {
  left: 22px;
  right: auto;
}

.tooltip.tooltip--below {
  top: calc(100% + 15px);
  bottom: auto;
}

.tooltip.tooltip--below::after {
  top: -12px;
  border-color: transparent transparent #2c3e50 transparent;
}

.floating-repair-select-button {
  padding: 0;
  background: white;
  border-radius: 50%;
  border: none;
  width: 100%;
  height: 100%;
  cursor: pointer;
  box-shadow: 0 4px 15px rgba(0, 0, 0, 0.15);
  transition: transform 0.3s cubic-bezier(0.175, 0.885, 0.32, 1.275),
    box-shadow 0.3s ease;
  overflow: hidden;
}

.floating-repair-select-button img {
  width: 100%;
  height: 100%;
  object-fit: contain;
  padding: 8px;
  pointer-events: none;
}

.floating-repair-select-button:hover {
  background: white;
  box-shadow: 0 6px 20px rgba(0, 0, 0, 0.2);
  transform: translateY(-4px) scale(1.05);
}

.floating-repair-select-button:active {
  transform: translateY(0) scale(0.95);
  box-shadow: 0 2px 10px rgba(0, 0, 0, 0.15);
}
</style>
